/**
 * The one place that touches `localStorage` (architecture §16.2, lint `no-restricted-globals`). Every read and write
 * is wrapped: storage can be disabled, full or throw in private windows, and a preference is never worth a crash.
 *
 * Keys (ADR-0039: per-account values carry the account id, so two signed-in accounts never read each other's):
 *   qd.theme            {"mode","fontSize","accountId"}  read before first paint by the inline boot script
 *   qd.sidebar          {"width","collapsed"}            docked sidebar width (200–320) and collapsed state
 *   qd.sidebar.sections {"<section id>": true}           collapsed sidebar sections and team rows
 *   qd.recent:<account> ["command.id", …]                recently run palette commands (newest first)
 *   qd.labels.showTeamLabels:<account> true              workspace labels page: "Show team labels" (team-workflow-labels)
 *   qd.documents.view   "list" | "grid"                    the Documents tab's folder layout (documents-drive, ADR-0075)
 *   qd.e2e.onboarding   off (a raw string, not JSON)        set by the E2E harness: no welcome or Getting started
 *                                                          (onboarding, ADR-0080); read only by dev and test builds
 */

const PREFIX = 'qd.';

function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    return s ?? null;
  } catch {
    return null;
  }
}

function readPref<T>(key: string, parse: (raw: unknown) => T | undefined): T | undefined {
  const s = storage();
  if (!s) return undefined;
  try {
    const raw = s.getItem(PREFIX + key);
    if (raw === null) return undefined;
    return parse(JSON.parse(raw) as unknown);
  } catch {
    return undefined;
  }
}

function writePref(key: string, value: unknown): void {
  const s = storage();
  if (!s) return;
  try {
    if (value === undefined) s.removeItem(PREFIX + key);
    else s.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // quota or disabled storage: the preference simply isn't remembered
  }
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

// ---------------------------------------------------------------------------------------------------------------
// typed preferences

export type ThemeMode = 'system' | 'light' | 'dark';
export type FontSize = 'small' | 'default' | 'large';

export interface ThemePref {
  mode: ThemeMode;
  fontSize: FontSize;
  accountId?: string;
  /** When the local choice was made (ms since epoch); the server copy wins only when newer. */
  at?: number;
}

export const THEME_MODES: readonly ThemeMode[] = ['system', 'light', 'dark'];
const FONT_SIZES: readonly FontSize[] = ['small', 'default', 'large'];

export function readThemePref(): ThemePref {
  return (
    readPref('theme', (v) => {
      if (!isObj(v)) return undefined;
      const mode = THEME_MODES.includes(v.mode as ThemeMode) ? (v.mode as ThemeMode) : 'system';
      const fontSize = FONT_SIZES.includes(v.fontSize as FontSize) ? (v.fontSize as FontSize) : 'default';
      return {
        mode,
        fontSize,
        ...(typeof v.accountId === 'string' ? { accountId: v.accountId } : {}),
        ...(typeof v.at === 'number' ? { at: v.at } : {}),
      };
    }) ?? { mode: 'system', fontSize: 'default' }
  );
}

export function writeThemePref(p: ThemePref): void {
  writePref('theme', p);
}

export interface SidebarPref {
  width: number;
  collapsed: boolean;
}

export const SIDEBAR_MIN = 200;
export const SIDEBAR_MAX = 320;
const SIDEBAR_DEFAULT = 240;

export function clampSidebarWidth(w: number): number {
  if (!Number.isFinite(w)) return SIDEBAR_DEFAULT;
  return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.round(w)));
}

export function readSidebarPref(): SidebarPref {
  return (
    readPref('sidebar', (v) =>
      isObj(v)
        ? {
            width: clampSidebarWidth(typeof v.width === 'number' ? v.width : SIDEBAR_DEFAULT),
            collapsed: v.collapsed === true,
          }
        : undefined,
    ) ?? { width: SIDEBAR_DEFAULT, collapsed: false }
  );
}

export function writeSidebarPref(p: SidebarPref): void {
  writePref('sidebar', p);
}

export function readCollapsedSections(): Record<string, boolean> {
  return (
    readPref('sidebar.sections', (v) => {
      if (!isObj(v)) return undefined;
      const out: Record<string, boolean> = {};
      for (const [k, x] of Object.entries(v)) if (typeof x === 'boolean') out[k] = x;
      return out;
    }) ?? {}
  );
}

export function writeCollapsedSections(v: Record<string, boolean>): void {
  writePref('sidebar.sections', v);
}

const RECENT_MAX = 8;

export function readRecentCommands(accountId: string): string[] {
  return (
    readPref(`recent:${accountId}`, (v) =>
      Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, RECENT_MAX) : undefined,
    ) ?? []
  );
}

export function pushRecentCommand(accountId: string, id: string): string[] {
  const next = [id, ...readRecentCommands(accountId).filter((x) => x !== id)].slice(0, RECENT_MAX);
  writePref(`recent:${accountId}`, next);
  return next;
}

/**
 * "Show team labels" on the workspace labels page (registry issues.labels.settings AC3), per account; undefined
 * when never set (the page applies its default).
 */
export function readShowTeamLabels(accountId: string): boolean | undefined {
  return readPref(`labels.showTeamLabels:${accountId}`, (v) => (typeof v === 'boolean' ? v : undefined));
}

export function writeShowTeamLabels(accountId: string, show: boolean): void {
  writePref(`labels.showTeamLabels:${accountId}`, show);
}

/** The composer's "Create more" switch, per account on this device (registry issues.create-more AC3); undefined = never set. */
export function readComposerCreateMore(accountId: string): boolean | undefined {
  return readPref(`composer.createMore:${accountId}`, (v) => (typeof v === 'boolean' ? v : undefined));
}

export function writeComposerCreateMore(accountId: string, on: boolean): void {
  writePref(`composer.createMore:${accountId}`, on);
}

/** The Documents tab's folder layout (documents-drive; a per-viewer convenience, default list). */
export function readDocumentsView(): 'list' | 'grid' {
  return readPref('documents.view', (v) => (v === 'grid' || v === 'list' ? v : undefined)) ?? 'list';
}

export function writeDocumentsView(v: 'list' | 'grid'): void {
  writePref('documents.view', v);
}

/** The E2E harness's switch for the welcome and the checklist (ADR-0080). Its caller reads it in dev/test builds only. */
export function readOnboardingE2EOff(): boolean {
  try {
    return storage()?.getItem(`${PREFIX}e2e.onboarding`) === 'off';
  } catch {
    return false;
  }
}

/** E2E: Ask Quadratic's launcher stays out of every spec unless it opts in (localStorage `qd.e2e.assistant` = 'off'). */
export function readAssistantE2EOff(): boolean {
  try {
    return storage()?.getItem(`${PREFIX}e2e.assistant`) === 'off';
  } catch {
    return false;
  }
}
